import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { memo, useCallback, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { Maximize2, Minimize2, Workflow } from "lucide-react";
import { Button } from "../../base/button/button";
import { createLazyModuleLoader, useLazyModule } from "../../../utils/lazy-module";
import { VisualizationFullscreenOverlay } from "../visualization-fullscreen-overlay";
import { ChatEntityViewSkeleton, ChatEntityViewStateNotice, ChatProcessRecordCard } from "./chat-entity-view-shell";
/**
 * A whole process as its diagram, inside the transcript (plan 016 §6).
 *
 * Three things make this view different from the other embedded views:
 *
 *  - **It is the only one that is not free.** The canvas costs `@xyflow/react`
 *    plus `elkjs`, so it is reached through a dynamic import —
 *    `chat-process-flow-chunk.ts` owns that boundary.
 *  - **It must draw the SAME picture as the process page.** The saved layout is
 *    fetched alongside the process and threaded in; without it ELK re-lays-out
 *    the graph and the chat contradicts the page one click away.
 *  - **It sits in scrolling text.** React Flow's default wheel handling would
 *    freeze the transcript, which is what `scrollBehavior` exists for.
 */ /** The inline box. Tall enough to read a lane, short enough not to own the turn. */ const INLINE_HEIGHT = "360px";
const loadProcessFlowCanvas = createLazyModuleLoader(()=>import("./chat-process-flow-chunk").then((module)=>module.ProcessFlowVisualization));
/**
 * The published process, drawn by the product's own canvas.
 *
 * Memoised on its id for the same reason the other views are: the assistant
 * keeps emitting text after the part lands, and re-rendering this one costs a
 * layout pass rather than a diff.
 */ export const ProcessFlowView = /*#__PURE__*/ memo(function ProcessFlowView({ data, processId, slug }) {
    const { t } = useTranslation();
    const [isFullscreen, setIsFullscreen] = useState(false);
    const closeFullscreen = useCallback(()=>setIsFullscreen(false), []);
    const process = data.useProcessForView(processId);
    // A REMOUNT optimization: on first mount the process is still `pending`, so
    // both requests go out together. What it saves is the mount that meets an
    // already-cached 404, where the layout request would be a guaranteed second
    // failure. `unavailable` rather than `!== 'ready'`, because an ERRORED process
    // is worth retrying with its layout and `pending` must not gate the fetch.
    const layout = data.useProcessLayoutForView(processId, process.status !== "unavailable");
    const chunk = useLazyModule(loadProcessFlowCanvas, "Process flow chunk");
    // The early returns below tear the overlay down but would leave the FLAG set,
    // so a process that 404s while fullscreen is open and then refetches would pop
    // the overlay back with nobody having clicked. A state adjustment during
    // render, which settles in one pass: clearing the flag falsifies its own
    // condition.
    if (isFullscreen && process.status !== "ready") setIsFullscreen(false);
    if (process.status !== "ready") return /*#__PURE__*/ _jsx(ChatEntityViewStateNotice, {
        state: process
    });
    if (chunk.status !== "ready") return /*#__PURE__*/ _jsx(ChatEntityViewStateNotice, {
        state: chunk
    });
    // The layout is a gate: rendering before it lands means ELK lays the graph out
    // once and the saved positions then move every node, re-arranging a diagram
    // whose whole promise is that it matches the page. An ERRORED layout releases
    // the gate, ELK's arrangement being the best answer left. Stricter than
    // `ProcessDetailPage`, which paints ELK first and snaps: a page the user
    // navigated to can afford that, a diagram appearing mid-transcript cannot.
    if (layout.isPending) return /*#__PURE__*/ _jsx(ChatEntityViewSkeleton, {});
    const Canvas = chunk.module;
    const { process: record, capabilities, openAssignments, events, notes } = process.data;
    const canvas = (fullscreen)=>/*#__PURE__*/ _jsx(Canvas, {
            process: record,
            // Whatever box it is put in: inline that is `INLINE_HEIGHT`, fullscreen
            // it is the overlay's own flex child. One owner for the inline height.
            height: "100%",
            // Inline, the transcript owns the wheel; fullscreen, the overlay owns the
            // viewport and there is nothing behind it left to scroll.
            scrollBehavior: fullscreen ? "zoom" : "page",
            // The sidebar is 400px wide and the minimap eats a corner — both are
            // unusable in a 360px box, and both are what fullscreen is for.
            showStepDetail: fullscreen,
            showMinimap: fullscreen,
            showControls: true,
            showSensitiveFields: capabilities.canViewSensitiveFields,
            showVariantCheck: capabilities.canViewProcessInsights,
            savedPositions: layout.savedPositions,
            swimlaneOrder: layout.swimlaneOrder,
            laneColors: layout.laneColors,
            openAssignments: openAssignments,
            events: events,
            notes: notes,
            assigneeLookup: process.assigneeLookup,
            entitySuggestions: process.entitySuggestions
        });
    // The inline canvas UNMOUNTS behind the overlay, inside a box that keeps its
    // height so the card never collapses (`ProcessConnectionsMapCard`'s shape).
    // The remount is wanted: it re-runs `fitView` at the new viewport size, and
    // `useFlow` keys its ELK cache on values identical between the two instances,
    // so neither open nor close pays a layout pass.
    //
    // The overlay is NOT modal: `VisualizationFullscreenOverlay` has no
    // `role="dialog"`, no focus trap and no `inert`, so the transcript, the
    // composer and this card's own button stay tabbable behind it — a gap it
    // shares with its other consumers.
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx(ChatProcessRecordCard, {
                data: data,
                name: record.name,
                updatedAt: record.updatedAt,
                processId: processId,
                slug: slug,
                actions: /*#__PURE__*/ _jsxs(Button, {
                    variant: "outline",
                    size: "small",
                    onClick: ()=>setIsFullscreen(true),
                    children: [
                        /*#__PURE__*/ _jsx(Maximize2, {
                            className: "h-3.5 w-3.5",
                            "aria-hidden": true
                        }),
                        t("chat.entityView.openFullscreen")
                    ]
                }),
                children: /*#__PURE__*/ _jsx("div", {
                    style: {
                        height: INLINE_HEIGHT
                    },
                    children: !isFullscreen && canvas(false)
                })
            }),
            isFullscreen && /*#__PURE__*/ createPortal(/*#__PURE__*/ _jsx(VisualizationFullscreenOverlay, {
                icon: Workflow,
                title: record.name,
                onClose: closeFullscreen,
                controls: /*#__PURE__*/ _jsxs(Button, {
                    variant: "outline",
                    size: "small",
                    onClick: closeFullscreen,
                    children: [
                        /*#__PURE__*/ _jsx(Minimize2, {
                            className: "h-3.5 w-3.5",
                            "aria-hidden": true
                        }),
                        t("chat.entityView.exitFullscreen")
                    ]
                }),
                children: canvas(true)
            }), document.body)
        ]
    });
});

//# sourceMappingURL=chat-process-flow-view.js.map